import React from 'react'
import { Text } from 'sutra/components/Text/Text'

import type { FilterOption } from 'features/filters/types'
import {
  ScopeTooltip,
  ScopeTooltipContent,
  ScopeTooltipLabel,
} from 'features/trend-detection/components/scope-tooltip-styles'
import { summarizeCriteria } from 'features/trend-detection/features/issue-agent-detail/components/AgentInfoCard/filter-chips'
import {
  CardHeaderRow,
  CardWrap,
  ChipRow,
  EmptyChipText,
  HeaderTextBlock,
  IconTile,
  ScopeChip,
  Section,
  SectionTitle,
  SectionTitleText,
  TitleText,
} from 'features/trend-detection/features/issue-agent-detail/components/AgentInfoCard/styles'
import { ConversionPathIcon } from 'features/trend-detection/features/issue-agent-detail/components/SectionIcons'
import type { FilterCriterion } from 'features/trend-detection/types'

export interface AgentInfoCardHeader {
  label: string
  icon?: React.ReactNode
  description?: string
}

export interface AgentInfoCardSection {
  icon: React.ReactNode
  title: string
  filters: FilterCriterion[]
  emptyText: string
}

export interface AgentInfoCardProps {
  header?: AgentInfoCardHeader
  instructions?: string | null
  sections: AgentInfoCardSection[]
  /** Resolves UUID filter values to pretty names in scope chips. */
  filterOptions?: FilterOption[]
}

export const AgentInfoCard: React.FC<AgentInfoCardProps> = ({
  header,
  instructions,
  sections,
  filterOptions,
}) => {
  return (
    <CardWrap>
      {header && (
        <CardHeaderRow>
          {header.icon && <IconTile>{header.icon}</IconTile>}
          <HeaderTextBlock>
            <TitleText forwardedAs="h3">{header.label}</TitleText>
            {header.description && <Text $size="sm">{header.description}</Text>}
          </HeaderTextBlock>
        </CardHeaderRow>
      )}

      {instructions !== undefined && (
        <Section>
          <SectionTitle>
            <ConversionPathIcon />
            <SectionTitleText forwardedAs="span">Instructions</SectionTitleText>
          </SectionTitle>
          <Text $size="sm">{instructions ?? 'No instructions provided.'}</Text>
        </Section>
      )}

      {sections.map((section) => {
        const chips = summarizeCriteria(section.filters, filterOptions)
        return (
          <Section key={section.title}>
            <SectionTitle>
              {section.icon}
              <SectionTitleText forwardedAs="span">
                {section.title}
              </SectionTitleText>
            </SectionTitle>
            {chips.length === 0 ? (
              <EmptyChipText>{section.emptyText}</EmptyChipText>
            ) : (
              <ChipRow>
                {chips.map((chip) => (
                  <ScopeTooltip
                    key={chip.key}
                    content={
                      <ScopeTooltipContent>
                        <ScopeTooltipLabel>{chip.fieldLabel}</ScopeTooltipLabel>
                        {chip.singleLine ? (
                          <div>{chip.singleLine}</div>
                        ) : (
                          <ul>
                            {chip.values.map((v, i) => (
                              <li key={`${chip.key}-${i}`}>{v}</li>
                            ))}
                          </ul>
                        )}
                      </ScopeTooltipContent>
                    }
                    delay={400}
                  >
                    <ScopeChip forwardedAs="span">{chip.label}</ScopeChip>
                  </ScopeTooltip>
                ))}
              </ChipRow>
            )}
          </Section>
        )
      })}
    </CardWrap>
  )
}
